// Slide-down banner asking the user to enable notifications — appears once
// inside the app shell when permission is still 'default'. Premium-quality UX,
// not annoying.
import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Bell, X } from "lucide-react";
import { useAuth } from "../contexts/AuthContext";
import { registerForPush } from "../lib/firebase";
import { toast } from "sonner";

const DISMISS_KEY = "glow-notify-dismissed";

export default function NotificationsBanner() {
  const { user } = useAuth();
  const [show, setShow] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!user?.id) return;
    if (typeof Notification === "undefined") return;
    if (Notification.permission !== "default") return;
    if (localStorage.getItem(DISMISS_KEY)) return;
    // Delay 4s so the banner doesn't crash into the first page load.
    const t = setTimeout(() => setShow(true), 4000);
    return () => clearTimeout(t);
  }, [user?.id]);

  const enable = async () => {
    setBusy(true);
    const token = await registerForPush(user.id);
    setBusy(false);
    setShow(false);
    if (token) {
      toast.success("Notifications enabled", {
        description: "We'll nudge you with morning rituals & insights.",
      });
    } else if (Notification.permission === "denied") {
      toast.error("Notifications blocked", {
        description: "You can re-enable them in your browser settings.",
      });
    }
  };

  const dismiss = () => {
    localStorage.setItem(DISMISS_KEY, "1");
    setShow(false);
  };

  return (
    <AnimatePresence>
      {show && (
        <motion.div
          initial={{ y: -20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: -20, opacity: 0 }}
          transition={{ duration: 0.3 }}
          className="mx-5 mb-3 rounded-[20px] bg-white shadow-card p-4 flex items-start gap-3"
          data-testid="notifications-banner"
        >
          <div
            className="w-9 h-9 rounded-full flex items-center justify-center shrink-0"
            style={{ background: "linear-gradient(135deg, #C9748A 0%, #E8B4C2 100%)" }}
          >
            <Bell size={16} className="text-white" />
          </div>
          <div className="flex-1">
            <p className="text-sm font-medium text-cocoa mb-0.5">Stay in your glow rhythm</p>
            <p className="text-xs text-cocoa/60 font-light leading-snug mb-2.5">
              Get gentle 7am morning reminders, streak nudges, and your daily AI insight.
            </p>
            <div className="flex gap-2">
              <button
                onClick={enable}
                disabled={busy}
                data-testid="notifications-enable"
                className="rounded-full bg-rose hover:bg-rose-dark text-white text-xs px-4 py-2 font-medium disabled:opacity-50"
              >
                {busy ? "Enabling…" : "Turn on"}
              </button>
              <button
                onClick={dismiss}
                data-testid="notifications-dismiss"
                className="rounded-full bg-cream text-cocoa/70 text-xs px-4 py-2 font-medium"
              >
                Not now
              </button>
            </div>
          </div>
          <button onClick={dismiss} className="text-cocoa/30 hover:text-cocoa">
            <X size={16} />
          </button>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
